"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updatePlannedShipmentQuantitySchema = exports.createPlannedShipmentSchema = exports.setPlannedShipmentExpectedLoadingDateSchema = exports.setOrderAgreedPriceSchema = exports.ORDER_AGREED_PRICE_SIDES = exports.setOrderOperationsMemberSchema = exports.ORDER_OPERATIONS_MEMBER_SLOTS = exports.setOrderTraderSchema = exports.orderDetailResponseSchema = exports.ORDER_PAYMENT_STATUS_LABELS = exports.ORDER_FINANCEABILITY_LABELS = exports.ORDER_SHIPMENT_STATUS_LABELS = exports.ORDER_WARNING_SEVERITIES = exports.ORDER_WARNING_RULE_KEYS = exports.HEDGING_ORDER_WARNING_RULE_KEYS = exports.BL_ORDER_WARNING_RULE_KEYS = exports.COUNTERPARTY_SIGNED_SO_CATEGORY = exports.COUNTERPARTY_SIGNED_PO_CATEGORY = exports.COMPLETED_BL_INSTRUCTIONS_CATEGORY = exports.BL_GROUP_DOCS_STATUS = exports.BL_ORDER_STATUS = exports.orderDetailSignatureSchema = exports.SIGNATURE_STATUSES = exports.ORDER_FOUR_EYE_STATUS = exports.orderFilterValuesSchema = exports.orderFilterCounterpartySchema = exports.orderGroupedResponseSchema = exports.orderCommodityGroupSchema = exports.orderListResponseSchema = exports.orderListItemSchema = exports.orderListItemDetailSchema = exports.orderListQuerySchema = exports.ORDER_LIST_ORDER = exports.ORDER_LIST_SORT = exports.ORDER_LIST_PAGE_SIZES = exports.ORDER_COMMODITY = exports.tollingFields = exports.CURRENCY = exports.TOLLING_LEG_LABEL = exports.TOLLING_LEG = exports.ORDER_TYPE = exports.ORDER_STATUS = exports.COMPUTED_ORDER_STATUS = void 0;
exports.refineTollingQuad = refineTollingQuad;
exports.normalizeTollingQuad = normalizeTollingQuad;
const zod_1 = require("zod");
const common_1 = require("./common");
// Wire-format enum values match Prisma's TS-side identifiers (the keys in
// `@prisma/client`'s generated enum const), not the DB-stored `@map` strings.
// Prisma's runtime layer translates between the two — JS code only ever sees
// the TS-side identifiers. The FE display layer is responsible for mapping
// these to human-readable strings (e.g. `Preparing_Cargo` → "Preparing Cargo").
//
// `@metycle/shared` is Prisma-free so the FE can import it, so these lists are
// hand-mirrored. Drift is caught by
// `backend/src/features/orders/schemas/order-list-query.schema.spec.ts`.
// The computed values are derived from approval / BL / claim state by the
// backend status recompute. Pending_Approval precedes Preparing_Cargo: an order
// whose inventory match still needs approval is not yet free to prepare cargo.
// Cancelled and Draft are never computed - they exist only on orders migrated
// from the launchpad (parity finding 2) and the recompute skips orders holding
// them.
exports.COMPUTED_ORDER_STATUS = [
    'Pending_Approval',
    'Preparing_Cargo',
    'In_Transit',
    'Claim_Mgmt',
    'Complete',
];
exports.ORDER_STATUS = [...exports.COMPUTED_ORDER_STATUS, 'Cancelled', 'Draft'];
exports.ORDER_TYPE = ['B2B', 'Inventory'];
// Tolling classification (P6): which leg of a netted two-leg tolling deal an
// order is. Null on the order means "not a tolling leg" — see the Fence
// contract §4.6 (`.product/data-tape/fence-api-contract.md`).
exports.TOLLING_LEG = ['buyleg', 'sellleg'];
exports.TOLLING_LEG_LABEL = {
    buyleg: 'Buy leg',
    sellleg: 'Sell leg',
};
// The central currency list, hand-mirrored from Prisma's `currency_enum`
// (drift-guarded next to the TOLLING_LEG mirror spec).
exports.CURRENCY = [
    'USD',
    'EUR',
    'GBP',
    'CNY',
    'JPY',
    'AUD',
    'CAD',
    'CHF',
    'INR',
    'MYR',
    'THB',
];
// Tolling quad (P6, card #1045): set manually at match time (and editable on
// the order for now). All-or-nothing — a tolling order
// carries a leg, a per-MT toll cost, and the cost's currency (saved
// separately from the order currency and possibly different from it); a
// non-tolling order carries none. Shared by the match-creation DTOs and the
// order-tolling PATCH.
exports.tollingFields = {
    isTolling: zod_1.z.boolean().optional(),
    tollingLeg: zod_1.z.enum(exports.TOLLING_LEG).optional(),
    // .finite() is load-bearing (Codex gate): coerce accepts "Infinity"/"1e309",
    // Prisma Decimal stores Infinity, and the tape's toNumber() then projects
    // the cost as null — a broken quad reaching the lender.
    tollingCostPerMt: zod_1.z.coerce.number().positive().finite().optional(),
    // Required with the cost — no default: the trader picks it consciously.
    tollingCostCurrency: zod_1.z.enum(exports.CURRENCY).optional(),
};
const QUAD_DEPENDENTS = ['tollingLeg', 'tollingCostPerMt', 'tollingCostCurrency'];
function refineTollingQuad(data, ctx) {
    for (const field of QUAD_DEPENDENTS) {
        if (data.isTolling === true && data[field] === undefined) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: [field],
                message: `${field} is required when isTolling is true`,
            });
        }
        if (data.isTolling !== true && data[field] !== undefined) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: [field],
                message: `${field} is only allowed when isTolling is true`,
            });
        }
    }
}
function normalizeTollingQuad(input) {
    if (input.isTolling !== true) {
        return {
            isTolling: false,
            tollingLeg: null,
            tollingCostPerMt: null,
            tollingCostCurrency: null,
        };
    }
    return {
        isTolling: true,
        tollingLeg: input.tollingLeg ?? null,
        tollingCostPerMt: input.tollingCostPerMt ?? null,
        tollingCostCurrency: input.tollingCostCurrency ?? null,
    };
}
exports.ORDER_COMMODITY = [
    'Aluminium',
    'Mixed_metals',
    'Zinc',
    'Magnesium',
    'Lead',
    'Nickel_stainless_hi_temp',
    'Copper',
    'Brass',
    'Steel',
    'Iron',
];
exports.ORDER_LIST_PAGE_SIZES = [10, 25, 50];
// Each entry must map to a top-level scalar column on the `order` table.
// Composite display fields (Buyer→Supplier, Route) and join-derived ones
// (Trader, Operations) aren't here — they'd need raw orderBy or joins and
// the disabled cells in the UI signal they're future-release anyway.
exports.ORDER_LIST_SORT = [
    'id',
    'status',
    'order_type',
    'commodity_type',
    'allocated_quantity_mt',
    'margin',
    'created_at',
];
exports.ORDER_LIST_ORDER = ['asc', 'desc'];
const createdMonthSchema = zod_1.z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');
const pageSizeSchema = zod_1.z.coerce
    .number()
    .int()
    .pipe(zod_1.z.union([zod_1.z.literal(10), zod_1.z.literal(25), zod_1.z.literal(50)]))
    .default(10);
exports.orderListQuerySchema = zod_1.z
    .object({
    search: common_1.optionalTrimmedString,
    commodity: zod_1.z.enum(exports.ORDER_COMMODITY).optional(),
    counterpartyId: zod_1.z.coerce.bigint().optional(),
    orderType: zod_1.z.enum(exports.ORDER_TYPE).optional(),
    status: zod_1.z.enum(exports.ORDER_STATUS).optional(),
    createdMonth: createdMonthSchema.optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: pageSizeSchema,
    sort: zod_1.z.enum(exports.ORDER_LIST_SORT).default('created_at'),
    order: zod_1.z.enum(exports.ORDER_LIST_ORDER).default('desc'),
})
    .strict();
const ticketRefSchema = zod_1.z.object({
    id: zod_1.z.string(),
});
exports.orderListItemDetailSchema = zod_1.z.object({
    isriGrade: zod_1.z.string().nullable(),
    metalForm: zod_1.z.string().nullable(),
    productDetails: zod_1.z.string().nullable(),
    buyPrice: zod_1.z.string().nullable(),
    sellPrice: zod_1.z.string().nullable(),
    buyPricingType: zod_1.z.string().nullable(),
    sellPricingType: zod_1.z.string().nullable(),
    buyCurrency: zod_1.z.string().nullable(),
    sellCurrency: zod_1.z.string().nullable(),
    signedDate: zod_1.z.string().nullable(),
    dealValue: zod_1.z.string().nullable(),
    dealCurrency: zod_1.z.string().nullable(),
    buyPaymentTerms: zod_1.z.string().nullable(),
    sellPaymentTerms: zod_1.z.string().nullable(),
    buyIncoterms: zod_1.z.string().nullable(),
    sellIncoterms: zod_1.z.string().nullable(),
    buyTicket: ticketRefSchema.nullable(),
    sellTicket: ticketRefSchema.nullable(),
});
exports.orderListItemSchema = zod_1.z.object({
    id: zod_1.z.string(),
    status: zod_1.z.enum(exports.ORDER_STATUS),
    orderType: zod_1.z.enum(exports.ORDER_TYPE),
    buyerName: zod_1.z.string().nullable(),
    supplierName: zod_1.z.string().nullable(),
    buyerCompanyId: zod_1.z.string().nullable(),
    commodity: zod_1.z.enum(exports.ORDER_COMMODITY).nullable(),
    quantityMt: zod_1.z.string().nullable(),
    shipFrom: zod_1.z.string().nullable(),
    shipTo: zod_1.z.string().nullable(),
    traderName: zod_1.z.string().nullable(),
    finalOperationsMember: zod_1.z.string().nullable(),
    secondOperationsMember: zod_1.z.string().nullable(),
    marginPct: zod_1.z.number().nullable(),
    createdAt: zod_1.z.string().nullable(),
    detail: exports.orderListItemDetailSchema,
});
exports.orderListResponseSchema = zod_1.z.object({
    items: zod_1.z.array(exports.orderListItemSchema),
    total: zod_1.z.number().int().min(0),
    page: zod_1.z.number().int().min(1),
    pageSize: zod_1.z.number().int(),
});
// Grid view: orders grouped by commodity, one group per metal. `count` is the
// group's true size; `orders` may be capped (see the backend cap) so the count
// can exceed `orders.length` on very large groups. `commodity` is null for the
// "Unspecified" lane (orders with no commodity resolved).
exports.orderCommodityGroupSchema = zod_1.z.object({
    commodity: zod_1.z.enum(exports.ORDER_COMMODITY).nullable(),
    count: zod_1.z.number().int().min(0),
    orders: zod_1.z.array(exports.orderListItemSchema),
});
exports.orderGroupedResponseSchema = zod_1.z.object({
    groups: zod_1.z.array(exports.orderCommodityGroupSchema),
    total: zod_1.z.number().int().min(0),
});
exports.orderFilterCounterpartySchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
});
exports.orderFilterValuesSchema = zod_1.z.object({
    counterparties: zod_1.z.array(exports.orderFilterCounterpartySchema),
    createdMonths: zod_1.z.array(createdMonthSchema),
});
// Detail-page response. Single GET /orders/:id call returns every value the
// detail page renders (header, trade contract, both material cards, container
// & BL summary aggregates + per-shipment list, documents, plus read-only
// state for the disabled-with-tooltip slots that are surfaced from `order`).
exports.ORDER_FOUR_EYE_STATUS = [
    'Pending',
    'Approved',
    'Rejected',
    'Changes Requested',
    'Not Requested',
];
// Mirrored from the backend's `SIGNATURE_STATUSES` (see
// `backend/src/features/signing/pandadoc-status-mapper.ts`). `@metycle/shared`
// is PandaDoc-free; drift is caught by `signature-statuses.drift.spec.ts`.
exports.SIGNATURE_STATUSES = [
    'draft',
    'sent',
    'viewed',
    'waiting_approval',
    'completed',
    'voided',
    'declined',
];
// One signing row from `document_signatures` joined to the order's PO or SO
// document. `signedDate` carries the override value when
// `signedDateManualOverride` is true; otherwise it mirrors the PandaDoc-derived
// signing date. `counterpartySignedFileId` is the FK to the counterparty-signed
// PDF uploaded against this row — null while Metycle has signed but the
// counterparty hasn't returned their copy. Frontend resolves the file's
// presigned URL via the signing-scope download route, not as part of this read.
exports.orderDetailSignatureSchema = zod_1.z.object({
    id: zod_1.z.string(),
    status: zod_1.z.enum(exports.SIGNATURE_STATUSES),
    sentAt: zod_1.z.string().nullable(),
    completedAt: zod_1.z.string().nullable(),
    signedDate: zod_1.z.string().nullable(),
    signedDateManualOverride: zod_1.z.boolean(),
    counterpartySignedFileId: zod_1.z.string().nullable(),
});
const paymentTriggerTimingSchema = zod_1.z.enum(['Before', 'After']);
const orderDetailPaymentTriggerSchema = zod_1.z.object({
    event: zod_1.z.string().nullable(),
    timing: paymentTriggerTimingSchema.nullable(),
    offsetDays: zod_1.z.string().nullable(),
});
const orderDetailMaterialCardSchema = zod_1.z.object({
    ticket: zod_1.z
        .object({
        id: zod_1.z.string(),
        pricingType: zod_1.z.string().nullable(),
        signedPrice: zod_1.z.string().nullable(),
        currency: zod_1.z.string().nullable(),
        incoterms: zod_1.z.string().nullable(),
        paymentTerms: zod_1.z.string().nullable(),
        productDetails: zod_1.z.string().nullable(),
        shipmentWindow: zod_1.z.string().nullable(),
        paymentTrigger: orderDetailPaymentTriggerSchema.nullable(),
        basis: zod_1.z.string().nullable(),
        payablePercent: zod_1.z.string().nullable(),
        premiumDiscount: zod_1.z.string().nullable(),
        referencePriceSource: zod_1.z.string().nullable(),
    })
        .nullable(),
    downpaymentAmount: zod_1.z.string().nullable(),
    downpaymentDueDate: zod_1.z.string().nullable(),
    fxRate: zod_1.z.string().nullable(),
    fxRateHedged: zod_1.z.boolean().nullable(),
});
const orderDocumentContactOptionSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string().nullable(),
    email: zod_1.z.string().nullable(),
    isPrimary: zod_1.z.boolean(),
});
const orderDetailPlannedShipmentSchema = zod_1.z.object({
    id: zod_1.z.string(),
    shipmentNumber: zod_1.z.string().nullable(),
    quantity: zod_1.z.string().nullable(),
    expectedLoadingDate: zod_1.z.string().nullable(),
    buyTicketId: zod_1.z.string().nullable(),
    blOrderId: zod_1.z.string().nullable(),
    blOrderName: zod_1.z.string().nullable(),
    createdByDisplayName: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string().nullable(),
});
// Live aggregate over the order's non-deleted child `bl_order` rows and
// `planned_shipment` rows. Recomputed on every detail-page request. Null on
// the numeric fields indicates "not computable" (e.g. allocated quantity not
// set on the order); zero indicates "computable and equals zero".
const orderDetailContainerBlSummarySchema = zod_1.z.object({
    totalOrderQuantityMt: zod_1.z.string().nullable(),
    numberOfBlGroups: zod_1.z.number().int().min(0),
    totalLoadedMt: zod_1.z.string(),
    percentFilled: zod_1.z.number().nullable(),
    remainingMt: zod_1.z.string().nullable(),
    plannedBlsCount: zod_1.z.number().int().min(0),
    nextEta: zod_1.z.string().nullable(),
});
// Per-BL-group projection used by the order detail page's BL Groups table.
// One entry per non-deleted child `bl_order`. Dates are date-only (`YYYY-MM-DD`),
// loaded MT is the BL's own `loaded_quantity_mt`, and `percentOfOrder` is that
// loaded MT divided by the parent order's `allocated_quantity_mt`. `docsStatus`
// answers "has the BL been issued" — `uploaded` when the BL carries a number
// and is no longer in draft, `missing` otherwise. Foundry stores no per-BL
// document URL on the `bl_order` table itself.
exports.BL_ORDER_STATUS = ['Draft', 'In_Transit', 'Complete'];
exports.BL_GROUP_DOCS_STATUS = ['uploaded', 'missing'];
const orderDetailBlGroupSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string().nullable(),
    blNumber: zod_1.z.string().nullable(),
    status: zod_1.z.enum(exports.BL_ORDER_STATUS).nullable(),
    route: zod_1.z.string().nullable(),
    etd: zod_1.z.string().nullable(),
    eta: zod_1.z.string().nullable(),
    ataPod: zod_1.z.string().nullable(),
    ataFinal: zod_1.z.string().nullable(),
    loadedMt: zod_1.z.string().nullable(),
    percentOfOrder: zod_1.z.number().nullable(),
    docsStatus: zod_1.z.enum(exports.BL_GROUP_DOCS_STATUS),
});
const orderDetailDocumentSchema = zod_1.z.object({
    id: zod_1.z.string(),
    fileName: zod_1.z.string(),
    fileUrl: zod_1.z.string(),
    createdByDisplayName: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string().nullable(),
    superseded: zod_1.z.boolean(),
});
// Category discriminator written to `document.category` for completed
// BL-instructions uploads on an order. Shared so backend writers and frontend
// readers agree on a single literal.
exports.COMPLETED_BL_INSTRUCTIONS_CATEGORY = 'completed_bl_instructions';
// Category discriminators written to `document.category` for the
// counterparty-signed PDF uploaded against a PO or SO document_signatures row.
// Each side gets its own category so the unified files+documents pipeline can
// resolve presigned URLs and supersession history independently.
exports.COUNTERPARTY_SIGNED_PO_CATEGORY = 'counterparty_signed_po';
exports.COUNTERPARTY_SIGNED_SO_CATEGORY = 'counterparty_signed_so';
// Warning rule keys are namespaced per owning feature: the `bl_*` keys are
// evaluated by the orders feature against BL rows, the `hedging_*` keys by the
// hedging feature's warnings export. Future scopes own their own namespaces
// (`ops_*`, …).
exports.BL_ORDER_WARNING_RULE_KEYS = [
    'bl_draft',
    'bl_missing_number',
    'tracking_error',
    'eta_overdue',
    'loaded_exceeds_order_qty',
];
exports.HEDGING_ORDER_WARNING_RULE_KEYS = [
    'hedging_price_not_fixed',
    'hedging_hedge_not_booked',
    'hedging_fx_not_hedged',
];
exports.ORDER_WARNING_RULE_KEYS = [
    ...exports.BL_ORDER_WARNING_RULE_KEYS,
    ...exports.HEDGING_ORDER_WARNING_RULE_KEYS,
];
exports.ORDER_WARNING_SEVERITIES = ['warning'];
const orderDetailWarningSchema = zod_1.z.object({
    key: zod_1.z.enum(exports.ORDER_WARNING_RULE_KEYS),
    title: zod_1.z.string(),
    body: zod_1.z.string(),
    blOrderId: zod_1.z.string().nullable(),
    severity: zod_1.z.enum(exports.ORDER_WARNING_SEVERITIES),
});
// Order-level shipment roll-up shown in the detail status strip. Derived from
// the order's live `bl_order` rows (arrival/departure dates). Null when the
// order has no BLs yet (the card renders an empty state). `delivered` /
// `inTransit` / `planned` partition the live BLs; `nextEta` is the earliest
// future ETA across them.
exports.ORDER_SHIPMENT_STATUS_LABELS = [
    'Preparing Cargo',
    'In Transit',
    'Partially Delivered',
    'All Delivered',
];
const orderDetailShipmentStatusSchema = zod_1.z.object({
    label: zod_1.z.enum(exports.ORDER_SHIPMENT_STATUS_LABELS),
    delivered: zod_1.z.number().int().min(0),
    inTransit: zod_1.z.number().int().min(0),
    planned: zod_1.z.number().int().min(0),
    nextEta: zod_1.z.string().nullable(),
});
// Order-level financeability roll-up shown in the detail status strip. Derived
// from the order's live `bl_order` rows: a BL is financed once a partner is
// assigned, eligible when the manual flag is set, otherwise not eligible.
// `partners` is the deduped set of assigned + manually-eligible partner names.
// Null when the order has no BLs yet (the card renders an empty state).
exports.ORDER_FINANCEABILITY_LABELS = [
    'All Financed',
    'Partially Financeable',
    'Not Financeable',
];
const orderDetailFinanceabilitySchema = zod_1.z.object({
    label: zod_1.z.enum(exports.ORDER_FINANCEABILITY_LABELS),
    financed: zod_1.z.number().int().min(0),
    eligible: zod_1.z.number().int().min(0),
    notEligible: zod_1.z.number().int().min(0),
    partners: zod_1.z.array(zod_1.z.string()),
});
// Order-level payment roll-up shown in the detail status strip. A settlement
// count over the order's BLs (how many are fully settled per the financing
// deal-status engine), not a money total. Null when the order has no BLs yet.
exports.ORDER_PAYMENT_STATUS_LABELS = ['Unpaid', 'Partially Paid', 'Fully Paid'];
const orderDetailPaymentStatusSchema = zod_1.z.object({
    label: zod_1.z.enum(exports.ORDER_PAYMENT_STATUS_LABELS),
    settled: zod_1.z.number().int().min(0),
    total: zod_1.z.number().int().min(0),
});
exports.orderDetailResponseSchema = zod_1.z.object({
    // Header
    id: zod_1.z.string(),
    status: zod_1.z.enum(exports.ORDER_STATUS),
    orderType: zod_1.z.enum(exports.ORDER_TYPE),
    buyerName: zod_1.z.string().nullable(),
    supplierName: zod_1.z.string().nullable(),
    buyerCompanyId: zod_1.z.string().nullable(),
    // Buy/sell trader from each side's ticket `trader_user`. *Name is display-ready
    // (full name, or email); *UserId drives the picker's selected value.
    buyTraderName: zod_1.z.string().nullable(),
    buyTraderUserId: zod_1.z.string().nullable(),
    sellTraderName: zod_1.z.string().nullable(),
    sellTraderUserId: zod_1.z.string().nullable(),
    finalOperationsMember: zod_1.z.string().nullable(),
    secondOperationsMember: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string().nullable(),
    // Trade contract section
    commodity: zod_1.z.enum(exports.ORDER_COMMODITY).nullable(),
    isriGrade: zod_1.z.string().nullable(),
    metalForm: zod_1.z.string().nullable(),
    quantityMt: zod_1.z.string().nullable(),
    shipFrom: zod_1.z.string().nullable(),
    shipTo: zod_1.z.string().nullable(),
    countryOfOrigin: zod_1.z.string().nullable(),
    partialShipmentAllowed: zod_1.z.boolean().nullable(),
    lastShippingDescription: zod_1.z.string().nullable(),
    // Buy-ticket goods description, copied onto the order at matching time. The
    // PO/SO generate dialog pre-fills its Goods field from this, and the PO/SO
    // templates render it on the GOODS line.
    productDetails: zod_1.z.string().nullable(),
    // Calendar day (YYYY-MM-DD) the sales order was signed. Drives the GMV
    // reporting period the order's value counts toward; editable via
    // PATCH /orders/:orderId/signed-date.
    salesOrderSignDate: zod_1.z.string().nullable(),
    // Tolling classification (P6, card #1045) — captured at match creation,
    // deliberately editable for now; currency saved separately from the
    // order currency (central currency list, mirrored as a plain string here to
    // keep @metycle/shared Prisma-free).
    isTolling: zod_1.z.boolean(),
    tollingLeg: zod_1.z.enum(exports.TOLLING_LEG).nullable(),
    tollingCostPerMt: zod_1.z.string().nullable(),
    tollingCostCurrency: zod_1.z.string().nullable(),
    // Material cards
    buy: orderDetailMaterialCardSchema,
    sell: orderDetailMaterialCardSchema,
    buyerContacts: zod_1.z.array(orderDocumentContactOptionSchema),
    supplierContacts: zod_1.z.array(orderDocumentContactOptionSchema),
    // Status strip — order-level read aggregates over the order's BLs.
    shipmentStatus: orderDetailShipmentStatusSchema.nullable(),
    paymentStatus: orderDetailPaymentStatusSchema.nullable(),
    financeability: orderDetailFinanceabilitySchema.nullable(),
    // Container & BL summary + container list
    containerBlSummary: orderDetailContainerBlSummarySchema,
    blGroups: zod_1.z.array(orderDetailBlGroupSchema),
    plannedShipments: zod_1.z.array(orderDetailPlannedShipmentSchema),
    // Documents (BL Instructions)
    documents: zod_1.z.array(orderDetailDocumentSchema),
    // BL-derived warnings followed by the hedging feature's warnings export;
    // ops-assignment warnings will append to this array when that scope ships.
    warnings: zod_1.z.array(orderDetailWarningSchema),
    // PO/SO four-eye state, derived from the latest order-hosted purchase_order /
    // sales_order document's approval status. The *DocumentId fields point at that
    // document (null when none has been generated yet) so the detail UI can offer
    // Generate vs Request-review and link through to the document.
    poFourEyeStatus: zod_1.z.enum(exports.ORDER_FOUR_EYE_STATUS).nullable(),
    soFourEyeStatus: zod_1.z.enum(exports.ORDER_FOUR_EYE_STATUS).nullable(),
    poDocumentId: zod_1.z.string().nullable(),
    soDocumentId: zod_1.z.string().nullable(),
    // Whether an enabled approval rule matches the latest generated PO/SO
    // document: true -> request-review opens a four-eye workflow; false ->
    // request-review auto-approves (no rule requires a review). Null when the
    // document is absent or past 'generated' (the only status where the
    // request-review action is offered).
    poReviewRequired: zod_1.z.boolean().nullable(),
    soReviewRequired: zod_1.z.boolean().nullable(),
    // PO/SO signing lifecycle. Latest non-voided `document_signatures` row keyed
    // on (reference_table='order', reference_id, document_type), or null when no
    // signing row exists yet. The frontend derives the lifecycle badge from
    // (fourEyeStatus, signature?.status, signature?.counterpartySignedFileId); a
    // present row implies the operator has clicked Start Signing.
    poSignature: exports.orderDetailSignatureSchema.nullable(),
    soSignature: exports.orderDetailSignatureSchema.nullable(),
    sharepointFolderUrl: zod_1.z.string().nullable(),
    sharepointFolderId: zod_1.z.string().nullable(),
});
// Body for PATCH /orders/:orderId/trader. Each side is an `app_users.id`; null
// clears that side, an omitted side is left unchanged.
const orderTraderUserIdSchema = zod_1.z.string().uuid('must be a user id').nullable();
exports.setOrderTraderSchema = zod_1.z
    .object({
    buyTraderUserId: orderTraderUserIdSchema.optional(),
    sellTraderUserId: orderTraderUserIdSchema.optional(),
})
    .refine((body) => body.buyTraderUserId !== undefined || body.sellTraderUserId !== undefined, {
    message: 'Provide buyTraderUserId and/or sellTraderUserId',
});
// Body for PATCH /orders/:orderId/operations-members. Assigns or clears the
// primary or secondary operations member on an order. `member` is one of the
// operations_member_enum values (kept as an enum for v1); `null` clears the
// slot. `slot` selects which column is written — primary →
// final_operations_member, secondary → second_operations_member. The member
// value is validated against the enum server-side, so it stays a plain string
// here and the schema does not have to mirror the enum's values.
exports.ORDER_OPERATIONS_MEMBER_SLOTS = ['primary', 'secondary'];
exports.setOrderOperationsMemberSchema = zod_1.z.object({
    slot: zod_1.z.enum(exports.ORDER_OPERATIONS_MEMBER_SLOTS),
    member: zod_1.z.string().min(1).nullable(),
});
// Body for POST /orders/:orderId/agreed-price. Sets a mutually-agreed price on
// one side of the order, applied across all the order's BLs on that side
// (order scope) and recorded in `bl_mutually_agreed_price_log`. `price` is a
// positive decimal string; the side must not be fixed-price.
exports.ORDER_AGREED_PRICE_SIDES = ['buy', 'sell'];
exports.setOrderAgreedPriceSchema = zod_1.z.object({
    side: zod_1.z.enum(exports.ORDER_AGREED_PRICE_SIDES),
    price: zod_1.z
        .string()
        .regex(/^\d+(\.\d{1,6})?$/, 'price must be a positive decimal string')
        .refine((value) => Number(value) > 0, { message: 'price must be greater than 0' }),
    currency: zod_1.z.string().min(1, 'currency is required'),
    note: zod_1.z.string().max(1000).optional(),
});
// Body for PATCH /orders/:orderId/planned-shipments/:plannedShipmentId.
// The date is the calendar day the loading is expected on, serialized as
// `YYYY-MM-DD`; `null` clears the column.
exports.setPlannedShipmentExpectedLoadingDateSchema = zod_1.z.object({
    expectedLoadingDate: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'expectedLoadingDate must be formatted YYYY-MM-DD')
        .nullable(),
});
// Decimal string > 0, up to 6 fractional digits. Matches Postgres's
// `Decimal` column on `planned_shipment.quantity_at_shipment_level`. The
// regex rejects negative signs and the strictly-positive check excludes
// "0", "0.0", and "0.00…". Both guards live on the same field schema so
// the object's inferred type stays a plain `{ quantityMt: string }`.
const positivePlannedShipmentQuantitySchema = zod_1.z
    .string()
    .regex(/^\d+(\.\d{1,6})?$/, 'quantityMt must be a positive decimal string')
    .refine((value) => Number(value) > 0, { message: 'quantityMt must be greater than 0' });
// Body for POST /orders/:orderId/planned-shipments. The shipment_number is
// server-assigned (max+1 over live rows) so it is not part of the body.
// expectedLoadingDate is optional — omit to create with no loading date set,
// or pass a `YYYY-MM-DD` string to seed it at creation time.
exports.createPlannedShipmentSchema = zod_1.z
    .object({
    quantityMt: positivePlannedShipmentQuantitySchema,
    expectedLoadingDate: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'expectedLoadingDate must be formatted YYYY-MM-DD')
        .optional(),
})
    .strict();
// Body for PATCH /orders/:orderId/planned-shipments/:plannedShipmentId/quantity.
exports.updatePlannedShipmentQuantitySchema = zod_1.z
    .object({ quantityMt: positivePlannedShipmentQuantitySchema })
    .strict();
//# sourceMappingURL=orders.js.map